<template>
  <div v-if="show" class="app-modal" role="presentation">
    <div class="app-modal__backdrop" @click="emitClose"></div>

    <section
      class="app-modal__dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="financial-account-modal-title"
      @click.stop
    >
      <header class="app-modal__header">
        <div>
          <h3 id="financial-account-modal-title">{{ modalTitle }}</h3>
          <p>{{ modalSubtitle }}</p>
        </div>
        <button
          class="app-modal__close"
          type="button"
          :aria-label="modalCloseAriaLabel"
          :disabled="saving"
          @click="emitClose"
        >
          <i class="fa fa-times" aria-hidden="true"></i>
        </button>
      </header>

      <div class="app-modal__body">
        <aside class="app-modal__menu" :aria-label="text.modal.createAccount.sectionMenuAriaLabel">
          <button
            v-for="section in visibleSections"
            :key="section.key"
            type="button"
            class="app-modal__menu-item"
            :class="{ active: activeSectionKey === section.key }"
            @click="activeSectionKey = section.key"
          >
            <span class="app-modal__menu-icon">
              <i class="fa" :class="section.icon" aria-hidden="true"></i>
            </span>
            <span class="app-modal__menu-copy">
              <strong>{{ section.title }}</strong>
              <small>{{ section.description }}</small>
            </span>
          </button>
        </aside>

        <div class="app-modal__content">
          <form class="app-modal__form" @submit.prevent="submitForm">
            <section v-show="activeSectionKey === 'general'" class="app-modal__section">
              <div class="app-modal__section-head">
                <h4>{{ text.modal.createAccount.sections.general.heading }}</h4>
                <p>{{ text.modal.createAccount.sections.general.subtitle }}</p>
              </div>

              <div class="app-modal__grid two-columns">
                <div class="app-modal-field full-width">
                  <div class="financial-account-modal__top-row">
                    <div class="app-modal-field">
                      <span>{{ text.modal.createAccount.fields.name }}</span>
                      <input
                        v-model.trim="form.name"
                        type="text"
                        class="form-control"
                        :class="{ 'is-invalid': hasFieldError('name') }"
                        :placeholder="text.modal.createAccount.placeholders.name"
                        :disabled="isFormLocked"
                        @blur="markTouched('name')"
                      />
                      <div v-if="hasFieldError('name')" class="invalid-feedback d-block">
                        {{ fieldErrors.name }}
                      </div>
                    </div>
                    <div class="app-modal-field">
                      <span>{{ text.modal.createAccount.fields.accountType }}</span>
                      <multiselect
                        v-model="typeModel"
                        :options="typeSelectorOptions"
                        track-by="value"
                        label="label"
                        :show-labels="false"
                        :searchable="true"
                        :allow-empty="false"
                        class="app-modal-select"
                        :class="{ 'is-invalid': hasFieldError('financial_account_type_id') }"
                        :placeholder="text.modal.createAccount.fields.accountTypePlaceholder"
                        :disabled="isFormLocked || catalogsLoading"
                        @close="markTouched('financial_account_type_id')"
                      >
                        <template #noResult>
                          <span>{{ text.modal.createAccount.messages.noOption }}</span>
                        </template>
                        <template #noOptions>
                          <span>{{ text.modal.createAccount.messages.noOption }}</span>
                        </template>
                      </multiselect>
                      <div v-if="hasFieldError('financial_account_type_id')" class="invalid-feedback d-block">
                        {{ fieldErrors.financial_account_type_id }}
                      </div>
                    </div>
                    <div class="app-modal-field">
                      <span>{{ text.modal.createAccount.fields.currency }}</span>
                      <multiselect
                        v-model="currencyModel"
                        :options="currencySelectorOptions"
                        track-by="value"
                        label="label"
                        :show-labels="false"
                        :searchable="true"
                        :allow-empty="false"
                        class="app-modal-select"
                        :placeholder="text.modal.createAccount.fields.currencyPlaceholder"
                        :disabled="isFormLocked || catalogsLoading"
                      >
                        <template #noResult>
                          <span>{{ text.modal.createAccount.messages.noOption }}</span>
                        </template>
                        <template #noOptions>
                          <span>{{ text.modal.createAccount.messages.noOption }}</span>
                        </template>
                      </multiselect>
                    </div>
                  </div>
                </div>

                <div class="app-modal-field">
                  <span>{{ text.modal.createAccount.fields.startDate }}</span>
                  <VueDatePicker
                    v-model="startDateModel"
                    class="app-modal-datepicker"
                    menu-class-name="app-modal-datepicker-menu"
                    :placeholder="text.modal.createAccount.fields.startDatePlaceholder"
                    :enable-time-picker="false"
                    auto-apply
                    locale="es-MX"
                    format="dd/MM/yyyy"
                    hide-input-icon
                    :show-now-button="true"
                    :now-button-label="text.modal.createAccount.messages.today"
                    :disabled="isFormLocked"
                  />
                </div>

                <div class="app-modal-field">
                  <span>{{ text.modal.createAccount.fields.startBalance }}</span>
                  <AppMoneyInput
                    v-model="form.start_balance"
                    output-mode="number"
                    :decimal-places="2"
                    :minimum-value="0"
                    :currency="String(form.currency || 'MXN').trim().toUpperCase() || 'MXN'"
                    :disabled="isFormLocked"
                    :placeholder="text.modal.createAccount.fields.amountPlaceholder"
                  />
                </div>

                <div v-if="showCreditFields" class="app-modal-field full-width">
                  <div class="app-modal__grid three-columns">
                    <div class="app-modal-field">
                      <span>{{ text.modal.createAccount.fields.creditLimit }}</span>
                      <AppMoneyInput
                        v-model="form.credit_limit"
                        output-mode="number"
                        :decimal-places="2"
                        :minimum-value="0"
                        :currency="String(form.currency || 'MXN').trim().toUpperCase() || 'MXN'"
                        :disabled="isFormLocked"
                        :placeholder="text.modal.createAccount.fields.amountPlaceholder"
                      />
                    </div>
                    <div class="app-modal-field">
                      <span>{{ text.modal.createAccount.fields.breakDay }}</span>
                      <input
                        v-model="form.break_day"
                        type="text"
                        class="form-control"
                        :class="{ 'is-invalid': hasFieldError('break_day') }"
                        inputmode="numeric"
                        :disabled="isFormLocked"
                        @input="onDayInput('break_day', $event)"
                        @blur="markTouched('break_day')"
                      />
                      <div v-if="hasFieldError('break_day')" class="invalid-feedback d-block">
                        {{ fieldErrors.break_day }}
                      </div>
                    </div>
                    <div class="app-modal-field">
                      <span>{{ text.modal.createAccount.fields.paymentDay }}</span>
                      <input
                        v-model="form.payment_day"
                        type="text"
                        class="form-control"
                        :class="{ 'is-invalid': hasFieldError('payment_day') }"
                        inputmode="numeric"
                        :disabled="isFormLocked"
                        @input="onDayInput('payment_day', $event)"
                        @blur="markTouched('payment_day')"
                      />
                      <div v-if="hasFieldError('payment_day')" class="invalid-feedback d-block">
                        {{ fieldErrors.payment_day }}
                      </div>
                    </div>
                  </div>
                </div>

                <div class="app-modal-field full-width financial-account-modal__configuration-block">
                  <span>{{ text.modal.createAccount.fields.configuration }}</span>
                  <div class="app-modal__grid two-columns financial-account-modal__configuration-grid">
                    <label class="app-modal-toggle">
                      <input v-model="form.is_default" type="checkbox" :disabled="isFormLocked" />
                      <span>{{ text.modal.createAccount.fields.isDefaultHelp }}</span>
                    </label>

                    <label v-if="showDepositField" class="app-modal-toggle">
                      <input v-model="form.is_account_payment" type="checkbox" :disabled="isFormLocked" />
                      <span>{{ text.modal.createAccount.fields.showDepositsHelp }}</span>
                    </label>
                  </div>
                </div>

                <div
                  v-if="showHolderField || showAccountNumberField || showCardNumberField || showClabeField"
                  class="app-modal-field full-width"
                >
                  <div class="app-modal__grid two-columns">
                    <div v-if="showHolderField" class="app-modal-field">
                      <span>{{ text.modal.createAccount.fields.holder }}</span>
                      <input
                        v-model.trim="form.holder"
                        type="text"
                        class="form-control"
                        :class="{ 'is-invalid': hasFieldError('holder') }"
                        :placeholder="text.modal.createAccount.placeholders.holder"
                        :disabled="isFormLocked"
                        @blur="markTouched('holder')"
                      />
                      <div v-if="hasFieldError('holder')" class="invalid-feedback d-block">
                        {{ fieldErrors.holder }}
                      </div>
                    </div>
                    <div v-if="showAccountNumberField" class="app-modal-field">
                      <span>{{ text.modal.createAccount.fields.accountNumber }}</span>
                      <input
                        v-model.trim="form.account_number"
                        type="text"
                        class="form-control"
                        :class="{ 'is-invalid': hasFieldError('account_number') }"
                        :placeholder="text.modal.createAccount.placeholders.accountNumber"
                        :disabled="isFormLocked"
                        @blur="markTouched('account_number')"
                      />
                      <div v-if="hasFieldError('account_number')" class="invalid-feedback d-block">
                        {{ fieldErrors.account_number }}
                      </div>
                    </div>
                    <div v-if="showCardNumberField" class="app-modal-field">
                      <span>{{ text.modal.createAccount.fields.cardNumber }}</span>
                      <input
                        v-model.trim="form.card_number"
                        type="text"
                        class="form-control"
                        :class="{ 'is-invalid': hasFieldError('card_number') }"
                        :placeholder="text.modal.createAccount.placeholders.cardNumber"
                        :disabled="isFormLocked"
                        @blur="markTouched('card_number')"
                      />
                      <div v-if="hasFieldError('card_number')" class="invalid-feedback d-block">
                        {{ fieldErrors.card_number }}
                      </div>
                    </div>
                    <div v-if="showClabeField" class="app-modal-field">
                      <span>{{ text.modal.createAccount.fields.clabe }}</span>
                      <input
                        v-model.trim="form.clabe"
                        type="text"
                        class="form-control"
                        :class="{ 'is-invalid': hasFieldError('clabe') }"
                        :placeholder="text.modal.createAccount.placeholders.clabe"
                        :disabled="isFormLocked"
                        @blur="markTouched('clabe')"
                      />
                      <div v-if="hasFieldError('clabe')" class="invalid-feedback d-block">
                        {{ fieldErrors.clabe }}
                      </div>
                    </div>
                  </div>
                </div>

                <div class="app-modal-field full-width">
                  <span>{{ text.modal.createAccount.fields.comments }}</span>
                  <textarea
                    v-model.trim="form.comments"
                    rows="4"
                    class="form-control app-modal-field__textarea"
                    :placeholder="text.modal.createAccount.fields.commentsPlaceholder"
                    :disabled="saving"
                  ></textarea>
                </div>
              </div>
            </section>

            <section v-show="activeSectionKey === 'bank' && showBankSection" class="app-modal__section">
              <div class="app-modal__section-head">
                <h4>{{ text.modal.createAccount.sections.bank.heading }}</h4>
                <p>{{ text.modal.createAccount.sections.bank.subtitle }}</p>
              </div>
              <div class="app-modal__grid two-columns">
                <div class="app-modal-field">
                  <span>{{ text.modal.createAccount.fields.bank }}</span>
                  <multiselect
                    v-model="bankModel"
                    :options="bankSelectorOptions"
                    track-by="value"
                    label="label"
                    :show-labels="false"
                    :searchable="true"
                    :allow-empty="true"
                    class="app-modal-select"
                    :placeholder="text.modal.createAccount.fields.bankPlaceholder"
                    :disabled="isFormLocked"
                  >
                    <template #noResult>
                      <span>{{ text.modal.createAccount.messages.noOption }}</span>
                    </template>
                    <template #noOptions>
                      <span>{{ text.modal.createAccount.messages.noOption }}</span>
                    </template>
                  </multiselect>
                </div>
                <div class="app-modal-field">
                  <span>{{ text.modal.createAccount.fields.bankRfc }}</span>
                  <input v-model.trim="form.bank_rfc" type="text" class="form-control" :placeholder="text.modal.createAccount.placeholders.bankRfc" :disabled="isFormLocked" />
                </div>
                <div class="app-modal-field">
                  <span>{{ text.modal.createAccount.fields.bankBranch }}</span>
                  <input v-model.trim="form.bank_branch" type="text" class="form-control" :placeholder="text.modal.createAccount.placeholders.bankBranch" :disabled="isFormLocked" />
                </div>
                <div class="app-modal-field">
                  <span>{{ text.modal.createAccount.fields.bankAddress }}</span>
                  <input v-model.trim="form.bank_address" type="text" class="form-control" :placeholder="text.modal.createAccount.placeholders.bankAddress" :disabled="isFormLocked" />
                </div>
                <div class="app-modal-field">
                  <span>{{ text.modal.createAccount.fields.swift }}</span>
                  <input v-model.trim="form.swift" type="text" class="form-control" :placeholder="text.modal.createAccount.placeholders.swift" :disabled="isFormLocked" />
                </div>
                <div class="app-modal-field">
                  <span>{{ text.modal.createAccount.fields.aba }}</span>
                  <input v-model.trim="form.aba" type="text" class="form-control" :placeholder="text.modal.createAccount.placeholders.aba" :disabled="isFormLocked" />
                </div>
              </div>
            </section>

            <p v-if="errorMessage" class="financial-account-modal__error">{{ errorMessage }}</p>

            <footer class="app-modal__footer">
              <div class="app-modal__footer-left">
                <button
                  v-if="isEditMode"
                  type="button"
                  class="app-table-icon-btn app-table-icon-btn--delete financial-account-modal__danger-btn"
                  :title="editDangerActionLabel"
                  :aria-label="editDangerActionLabel"
                  :disabled="saving"
                  @click="emitEditDangerAction"
                >
                  <i :class="editDangerActionIcon" aria-hidden="true"></i>
                </button>
                <button
                  v-if="isEditMode && !form.is_active"
                  type="button"
                  class="app-table-icon-btn app-table-icon-btn--delete financial-account-modal__danger-btn"
                  :title="deleteActionLabel"
                  :aria-label="deleteActionLabel"
                  :disabled="saving"
                  @click="emitEditDeleteAction"
                >
                  <i class="fa fa-trash" aria-hidden="true"></i>
                </button>
              </div>
              <div class="app-modal__footer-right">
                <button type="button" class="btn btn-light" :disabled="saving" @click="emitClose">{{ text.modal.createAccount.actions.cancel }}</button>
                <button type="submit" class="btn btn-primary" :disabled="saving">
                  {{ saving ? text.modal.createAccount.actions.saving : submitLabel }}
                </button>
              </div>
            </footer>
          </form>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import { useDomainText } from '@/composables/text/useDomainText';
import AppMoneyInput from '@/components/common/AppMoneyInput.vue';
import generalCatalogsService from '@/services/generalCatalogs';
import currenciesService from '@/services/currencies';
import { extractCollection } from '@/utils/dataNormalization';

const { catalog: text } = useDomainText('financial');

const todayIso = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const createInitialForm = () => ({
  financial_account_type_id: 0,
  name: '',
  currency: 'MXN',
  start_date: todayIso(),
  start_balance: '0.00',
  bank: '',
  bank_rfc: '',
  bank_branch: '',
  bank_address: '',
  holder: '',
  account_number: '',
  card_number: '',
  clabe: '',
  comments: '',
  swift: '',
  aba: '',
  break_day: 1,
  payment_day: 1,
  credit_limit: '0.00',
  is_default: false,
  is_account_payment: false,
  is_active: true,
});

export default {
  name: 'FinancialAccountModal',
  components: {
    VueDatePicker,
    AppMoneyInput,
  },
  props: {
    mode: {
      type: String,
      default: 'create',
    },
    show: {
      type: Boolean,
      default: false,
    },
    saving: {
      type: Boolean,
      default: false,
    },
    initialData: {
      type: Object,
      default: null,
    },
    initialSectionKey: {
      type: String,
      default: 'general',
    },
  },
  emits: ['close', 'submit', 'request-disable', 'request-enable', 'request-delete'],
  data() {
    return {
      activeSectionKey: 'general',
      errorMessage: '',
      formSubmitted: false,
      touchedFields: {},
      fieldErrors: {},
      text,
      catalogsLoading: false,
      typeCatalogOptions: [],
      bankCatalogOptions: [],
      currencyCatalogOptions: [],
      sections: [
        {
          key: 'general',
          title: text.modal.createAccount.sections.general.title,
          description: text.modal.createAccount.sections.general.description,
          icon: 'fa-id-card-o',
        },
        {
          key: 'bank',
          title: text.modal.createAccount.sections.bank.title,
          description: text.modal.createAccount.sections.bank.description,
          icon: 'fa-bank',
        },
      ],
      form: createInitialForm(),
    };
  },
  computed: {
    isEditMode() {
      return String(this.mode || 'create').trim().toLowerCase() === 'edit';
    },
    isInactiveEditMode() {
      return this.isEditMode && !this.form.is_active;
    },
    isFormLocked() {
      return this.saving || this.isInactiveEditMode;
    },
    submitLabel() {
      if (this.isEditMode) {
        return String(this.text?.modal?.editAccount?.actions?.save || this.text.modal.createAccount.actions.save).trim();
      }

      return this.text.modal.createAccount.actions.save;
    },
    modalTitle() {
      if (this.isEditMode) {
        return String(this.text?.modal?.editAccount?.title || this.text.modal.createAccount.title).trim();
      }

      return this.text.modal.createAccount.title;
    },
    modalSubtitle() {
      if (this.isEditMode) {
        return String(this.text?.modal?.editAccount?.subtitle || this.text.modal.createAccount.subtitle).trim();
      }

      return this.text.modal.createAccount.subtitle;
    },
    modalCloseAriaLabel() {
      if (this.isEditMode) {
        return String(this.text?.modal?.editAccount?.closeAriaLabel || this.text.modal.createAccount.closeAriaLabel).trim();
      }

      return this.text.modal.createAccount.closeAriaLabel;
    },
    editDangerActionLabel() {
      const actionText = this.form.is_active
        ? this.text?.modal?.editAccount?.actions?.disable
        : this.text?.modal?.editAccount?.actions?.enable;

      return String(actionText || '').trim();
    },
    editDangerActionIcon() {
      return this.form.is_active ? 'fa fa-ban' : 'fa fa-check';
    },
    deleteActionLabel() {
      return String(this.text?.modal?.editAccount?.actions?.delete || '').trim();
    },
    selectedTypeCode() {
      const currentId = Number(this.form?.financial_account_type_id || 0);
      const selected = this.typeSelectorOptions.find((option) => option.value === currentId);
      return String(selected?.code || '').trim().toUpperCase();
    },
    showDepositField() {
      return this.selectedTypeCode !== 'CASH';
    },
    showCreditFields() {
      return ['CREDIT_CARD', 'BANK_CREDIT', 'NOT_BANK_CREDIT'].includes(this.selectedTypeCode);
    },
    showBankSection() {
      return ['BANK', 'CREDIT_CARD', 'BANK_CREDIT'].includes(this.selectedTypeCode);
    },
    showHolderField() {
      return ['BANK', 'CREDIT_CARD', 'BANK_CREDIT'].includes(this.selectedTypeCode);
    },
    showAccountNumberField() {
      return ['BANK', 'BANK_CREDIT'].includes(this.selectedTypeCode);
    },
    showCardNumberField() {
      return this.selectedTypeCode === 'CREDIT_CARD';
    },
    showClabeField() {
      return this.selectedTypeCode === 'BANK';
    },
    requiredFieldMap() {
      const required = {
        name: true,
        financial_account_type_id: true,
      };

      if (this.selectedTypeCode === 'BANK') {
        required.holder = true;
        required.account_number = true;
        required.clabe = true;
      } else if (this.selectedTypeCode === 'CREDIT_CARD') {
        required.holder = true;
        required.card_number = true;
      } else if (this.selectedTypeCode === 'BANK_CREDIT') {
        required.holder = true;
        required.account_number = true;
      }

      return required;
    },
    visibleSections() {
      return this.sections.filter((section) => section.key !== 'bank' || this.showBankSection);
    },
    currencyModel: {
      get() {
        const currentCode = String(this.form?.currency || 'MXN').trim().toUpperCase() || 'MXN';
        return this.currencySelectorOptions.find((option) => option.value === currentCode)
          || this.currencySelectorOptions[0]
          || { value: 'MXN', label: 'MXN' };
      },
      set(option) {
        this.form.currency = String(option?.value || 'MXN').trim().toUpperCase() || 'MXN';
      },
    },
    startDateModel: {
      get() {
        return this.parseDateToObject(this.form?.start_date);
      },
      set(value) {
        this.form.start_date = this.formatDateToIso(value);
      },
    },
    typeSelectorOptions() {
      const source = Array.isArray(this.typeCatalogOptions) ? this.typeCatalogOptions : [];
      return [
        { value: 0, label: 'Selecciona tipo' },
        ...source.map((item) => ({
          value: Number(item?.id || 0),
          label: String(item?.name || '').trim(),
          code: String(item?.code || '').trim().toUpperCase(),
        })).filter((item) => item.value > 0 && item.label !== ''),
      ];
    },
    typeModel: {
      get() {
        const currentId = Number(this.form?.financial_account_type_id || 0);
        return this.typeSelectorOptions.find((option) => option.value === currentId)
          || this.typeSelectorOptions[0];
      },
      set(value) {
        this.form.financial_account_type_id = Number(value?.value || 0);
        this.applyTypeVisibilityRules();
      },
    },
    currencySelectorOptions() {
      const source = Array.isArray(this.currencyCatalogOptions) ? this.currencyCatalogOptions : [];
      const mapped = source
        .map((item) => {
          const code = String(item?.code || '').trim().toUpperCase();
          if (code === '') {
            return null;
          }

          return {
            value: code,
            label: code,
          };
        })
        .filter(Boolean);

      if (mapped.length === 0) {
        return [{ value: 'MXN', label: 'MXN' }];
      }

      const hasMxn = mapped.some((option) => option.value === 'MXN');
      return hasMxn ? mapped : [{ value: 'MXN', label: 'MXN' }, ...mapped];
    },
    bankSelectorOptions() {
      const source = Array.isArray(this.bankCatalogOptions) ? this.bankCatalogOptions : [];
      return source
        .map((item) => ({
          value: String(item?.id || '').trim(),
          label: String(item?.name || '').trim(),
        }))
        .filter((item) => item.value !== '' && item.label !== '');
    },
    bankModel: {
      get() {
        const current = String(this.form?.bank || '').trim();
        return this.bankSelectorOptions.find((option) => option.value === current) || null;
      },
      set(value) {
        this.form.bank = String(value?.value || '').trim();
      },
    },
  },
  watch: {
    async show(nextValue) {
      if (nextValue) {
        this.resetModalState();
        await this.loadCatalogs();
        if (this.isEditMode) {
          this.applyInitialData(this.initialData);
          this.applyTypeVisibilityRules();
          this.applyInitialSection();
        }
      }
    },
    initialData(nextValue) {
      if (!this.show || !this.isEditMode) {
        return;
      }

      this.applyInitialData(nextValue);
      this.applyTypeVisibilityRules();
      this.applyInitialSection();
    },
    initialSectionKey() {
      if (!this.show) {
        return;
      }

      this.applyInitialSection();
    },
    showBankSection(nextValue) {
      if (!nextValue && this.activeSectionKey === 'bank') {
        this.activeSectionKey = 'general';
      }
    },
  },
  methods: {
    async loadCatalogs() {
      if (this.catalogsLoading) {
        return;
      }

      this.catalogsLoading = true;
      try {
        const [typesResponse, banksResponse, currenciesResponse] = await Promise.all([
          generalCatalogsService.getFinancialAccountTypes(),
          generalCatalogsService.getBanks(),
          currenciesService.selectors(),
        ]);

        const types = extractCollection(typesResponse);
        const banks = extractCollection(banksResponse);
        const currencies = extractCollection(currenciesResponse);

        this.typeCatalogOptions = Array.isArray(types) ? types : [];
        this.bankCatalogOptions = Array.isArray(banks) ? banks : [];
        this.currencyCatalogOptions = Array.isArray(currencies) ? currencies : [];
        this.applyDefaultType();
        this.applyTypeVisibilityRules();
      } catch (error) {
        this.typeCatalogOptions = [];
        this.bankCatalogOptions = [];
        this.currencyCatalogOptions = [];
      } finally {
        this.catalogsLoading = false;
      }
    },
    applyDefaultType() {
      const currentId = Number(this.form?.financial_account_type_id || 0);
      if (currentId > 0) {
        return;
      }

      const cashOption = this.typeSelectorOptions.find((option) => option.code === 'CASH' && option.value > 0);
      const fallbackOption = this.typeSelectorOptions.find((option) => option.value > 0);
      const selected = cashOption || fallbackOption;

      if (!selected) {
        return;
      }

      this.form.financial_account_type_id = Number(selected.value || 0);
    },
    applyTypeVisibilityRules() {
      if (!this.showDepositField) {
        this.form.is_account_payment = false;
      }

      if (!this.showCreditFields) {
        this.form.credit_limit = 0;
        this.form.break_day = 1;
        this.form.payment_day = 1;
      }

      if (!this.showBankSection) {
        this.form.bank = '';
        this.form.bank_rfc = '';
        this.form.bank_branch = '';
        this.form.bank_address = '';
        this.form.holder = '';
        this.form.account_number = '';
        this.form.card_number = '';
        this.form.clabe = '';
        this.form.swift = '';
        this.form.aba = '';
      }

      this.clearHiddenFieldErrors();
    },
    markTouched(field) {
      this.touchedFields = {
        ...this.touchedFields,
        [field]: true,
      };
      this.validateForm();
    },
    hasFieldError(field) {
      const hasError = typeof this.fieldErrors[field] === 'string' && this.fieldErrors[field] !== '';
      if (!hasError) {
        return false;
      }

      return this.formSubmitted || this.touchedFields[field] === true;
    },
    validateForm() {
      const errors = {};
      const requiredMessage = String(this.text?.modal?.createAccount?.messages?.requiredField || 'Este campo es requerido').trim();
      const dayRangeMessage = String(this.text?.modal?.createAccount?.messages?.invalidDayRange || '').trim();
      const invalidNameMessage = String(this.text?.modal?.createAccount?.messages?.invalidNameFormat || requiredMessage).trim();

      const name = String(this.form?.name || '').trim();
      if (name === '') {
        errors.name = requiredMessage;
      } else if (!/^[\p{L}\p{N}](?:[\p{L}\p{N}\s.\-]*[\p{L}\p{N}])?$/u.test(name)) {
        errors.name = invalidNameMessage;
      }

      const typeId = Number(this.form?.financial_account_type_id || 0);
      if (!Number.isInteger(typeId) || typeId <= 0) {
        errors.financial_account_type_id = requiredMessage;
      }

      Object.entries(this.requiredFieldMap).forEach(([field, isRequired]) => {
        if (!isRequired || field === 'name' || field === 'financial_account_type_id') {
          return;
        }

        const value = String(this.form?.[field] || '').trim();
        if (value === '') {
          errors[field] = requiredMessage;
        }
      });

      const breakDayRaw = String(this.form?.break_day ?? '').trim();
      if (breakDayRaw !== '' && !this.isValidDay(Number(breakDayRaw))) {
        errors.break_day = dayRangeMessage;
      }

      const paymentDayRaw = String(this.form?.payment_day ?? '').trim();
      if (paymentDayRaw !== '' && !this.isValidDay(Number(paymentDayRaw))) {
        errors.payment_day = dayRangeMessage;
      }

      this.fieldErrors = errors;
      this.errorMessage = '';

      return Object.keys(errors).length === 0;
    },
    clearHiddenFieldErrors() {
      const keepFields = new Set(['name', 'financial_account_type_id', 'break_day', 'payment_day']);
      Object.entries(this.requiredFieldMap).forEach(([field, isRequired]) => {
        if (isRequired) {
          keepFields.add(field);
        }
      });

      const nextErrors = {};
      Object.keys(this.fieldErrors).forEach((field) => {
        if (keepFields.has(field)) {
          nextErrors[field] = this.fieldErrors[field];
        }
      });
      this.fieldErrors = nextErrors;

      const nextTouched = {};
      Object.keys(this.touchedFields).forEach((field) => {
        if (keepFields.has(field)) {
          nextTouched[field] = this.touchedFields[field];
        }
      });
      this.touchedFields = nextTouched;
    },
    parseBooleanFlag(value, fallback = false) {
      if (typeof value === 'boolean') {
        return value;
      }

      const normalized = String(value ?? '').trim().toLowerCase();
      if (normalized === '1' || normalized === 'true') {
        return true;
      }

      if (normalized === '0' || normalized === 'false') {
        return false;
      }

      return fallback;
    },
    applyInitialData(source) {
      const row = source && typeof source === 'object' ? source : null;
      if (!row) {
        return;
      }

      this.form = {
        ...createInitialForm(),
        financial_account_type_id: Number(row.financial_account_type_id || 0),
        name: String(row.name || '').trim(),
        currency: String(row.currency || 'MXN').trim().toUpperCase() || 'MXN',
        start_date: String(row.start_date || '').trim() || todayIso(),
        start_balance: String(row.start_balance ?? '0.00'),
        bank: String(row.bank || '').trim(),
        bank_rfc: String(row.bank_rfc || '').trim().toUpperCase(),
        bank_branch: String(row.bank_branch || '').trim(),
        bank_address: String(row.bank_address || '').trim(),
        holder: String(row.holder || '').trim(),
        account_number: String(row.account_number || '').trim(),
        card_number: String(row.card_number || '').trim(),
        clabe: String(row.clabe || '').trim(),
        comments: String(row.comments || '').trim(),
        swift: String(row.swift || '').trim().toUpperCase(),
        aba: String(row.aba || '').trim().toUpperCase(),
        break_day: String(row.break_day ?? 1),
        payment_day: String(row.payment_day ?? 1),
        credit_limit: String(row.credit_limit ?? '0.00'),
        is_default: this.parseBooleanFlag(row.is_default, false),
        is_account_payment: this.parseBooleanFlag(row.is_account_payment, false),
        is_active: this.parseBooleanFlag(row.is_active, true),
      };
    },
    parseDateToObject(value) {
      const raw = String(value || '').trim();
      if (raw === '') {
        return null;
      }

      const [year, month, day] = raw.split('-').map((part) => Number(part));
      if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) {
        return null;
      }

      const date = new Date(year, month - 1, day);
      return Number.isNaN(date.getTime()) ? null : date;
    },
    formatDateToIso(value) {
      if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
        return '';
      }

      const year = value.getFullYear();
      const month = String(value.getMonth() + 1).padStart(2, '0');
      const day = String(value.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    onDayInput(field, event) {
      const raw = String(event?.target?.value || '');
      const digitsOnly = raw.replace(/[^\d]/g, '');

      if (digitsOnly === '') {
        this.form[field] = '';
        return;
      }

      const parsed = Number(digitsOnly);
      if (!Number.isFinite(parsed)) {
        this.form[field] = '';
        return;
      }

      const clamped = Math.min(31, Math.max(1, parsed));
      this.form[field] = String(clamped);
    },
    isValidDay(value) {
      const parsed = Number(value);
      return Number.isInteger(parsed) && parsed >= 1 && parsed <= 31;
    },
    resetModalState() {
      this.activeSectionKey = 'general';
      this.errorMessage = '';
      this.formSubmitted = false;
      this.touchedFields = {};
      this.fieldErrors = {};
      this.form = createInitialForm();
    },
    normalizeSectionKey(sectionKey) {
      const normalized = String(sectionKey || '').trim().toLowerCase();
      return normalized === 'bank' ? 'bank' : 'general';
    },
    applyInitialSection() {
      const requested = this.normalizeSectionKey(this.initialSectionKey);
      if (requested === 'bank' && !this.showBankSection) {
        this.activeSectionKey = 'general';
        return;
      }

      this.activeSectionKey = requested;
    },
    emitEditDangerAction() {
      if (!this.isEditMode || this.saving) {
        return;
      }

      if (this.form.is_active) {
        this.$emit('request-disable');
        return;
      }

      this.$emit('request-enable');
    },
    emitEditDeleteAction() {
      if (!this.isEditMode || this.saving || this.form.is_active) {
        return;
      }

      this.$emit('request-delete');
    },
    emitClose() {
      if (this.saving) {
        return;
      }

      this.$emit('close');
    },
    submitForm() {
      if (this.isInactiveEditMode) {
        this.$emit('submit', {
          comments: String(this.form.comments || '').trim(),
        });
        return;
      }

      this.formSubmitted = true;
      const isValid = this.validateForm();
      const name = String(this.form.name || '').trim();
      const typeId = Number(this.form.financial_account_type_id || 0);

      if (!isValid) {
        this.activeSectionKey = 'general';
        return;
      }

      this.errorMessage = '';

      this.$emit('submit', {
        financial_account_type_id: typeId,
        name,
        currency: String(this.form.currency || 'MXN').trim().toUpperCase() || 'MXN',
        start_date: String(this.form.start_date || '').trim(),
        start_balance: this.form.start_balance,
        bank: String(this.form.bank || '').trim(),
        bank_rfc: String(this.form.bank_rfc || '').trim().toUpperCase(),
        bank_branch: String(this.form.bank_branch || '').trim(),
        bank_address: String(this.form.bank_address || '').trim(),
        holder: String(this.form.holder || '').trim(),
        account_number: String(this.form.account_number || '').trim(),
        card_number: String(this.form.card_number || '').trim(),
        clabe: String(this.form.clabe || '').trim(),
        comments: String(this.form.comments || '').trim(),
        swift: String(this.form.swift || '').trim().toUpperCase(),
        aba: String(this.form.aba || '').trim().toUpperCase(),
        break_day: Number(this.form.break_day || 1),
        payment_day: Number(this.form.payment_day || 1),
        credit_limit: this.form.credit_limit,
        is_default: this.form.is_default ? 1 : 0,
        is_account_payment: this.form.is_account_payment ? 1 : 0,
        is_active: this.form.is_active ? 1 : 0,
      });
    },
  },
};
</script>

<style scoped>
.app-modal__footer-left {
  margin-right: auto;
}

.financial-account-modal__error {
  margin: 0;
  font-size: 12px;
  color: #b91c1c;
}

.financial-account-modal__danger-btn {
  min-height: 38px;
  height: 38px;
  width: 44px;
  min-width: 44px;
  padding: 0 !important;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.financial-account-modal__top-row {
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(0, 1fr) minmax(0, 0.5fr);
  gap: 10px;
}

.financial-account-modal__configuration-grid {
  gap: 12px;
}

@media (max-width: 767px) {
  .financial-account-modal__top-row {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
